// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Database, FlaskConical, Download, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { AlertBadge, formatScoreCompact } from "./shared";

export default function ResearchView({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      if (filters.cancerType) params.append("cancer_type", filters.cancerType);
      if (filters.treatmentType) params.append("treatment_type", filters.treatmentType);
      params.append("page", page);
      params.append("page_size", 100);
      
      const res = await api.get(`/data/research?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des données recherche");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters, page]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      
      const res = await api.get(`/data/export/research?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `research_export_pseudonymized_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export recherche téléchargé");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-purple-600" />
            Données Recherche (Pseudonymisation forte)
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Dataset structuré sans identifiants directs (nom, téléphone, email)
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !data?.items?.length}
          data-testid="export-research-btn"
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Export..." : "Export CSV"}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-800">
        <strong>Pseudonymisation forte :</strong> aucune information nominative directe n'est incluse ici. 
        La qualification juridique finale dépend du périmètre, des consentements et de la gouvernance d'export.
      </div>

      {/* Data Table */}
      <div className="dash-card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">UUID Patient</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Date</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Cancer</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Traitement</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Ligne</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Fat.</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Doul.</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Naus.</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Diar.</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Dysp.</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">T°C</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">SpO2</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Alerte</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={13} className="p-8 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
                    Chargement...
                  </td>
                </tr>
              ) : data?.items?.length === 0 ? (
                <tr>
                  <td colSpan={13} className="p-8 text-center text-slate-400">
                    <Database className="w-8 h-8 mx-auto opacity-40 mb-2" />
                    Aucune donnée pour ces filtres
                  </td>
                </tr>
              ) : (
                data?.items?.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs text-slate-500">
                      {row.patient_uuid?.slice(-8)}
                    </td>
                    <td className="p-3 text-slate-600 whitespace-nowrap">
                      {row.observation_date ? new Date(row.observation_date).toLocaleDateString("fr-FR") : "—"}
                    </td>
                    <td className="p-3 text-slate-600">{row.cancer_type || "—"}</td>
                    <td className="p-3 text-slate-600">{row.treatment_type || "—"}</td>
                    <td className="p-3 text-xs text-slate-500">{row.therapeutic_line || "—"}</td>
                    <td className="p-3">{formatScoreCompact(row.fatigue_score)}</td>
                    <td className="p-3">{formatScoreCompact(row.douleur_score)}</td>
                    <td className="p-3">{formatScoreCompact(row.nausee_score)}</td>
                    <td className="p-3">{formatScoreCompact(row.diarrhee_score)}</td>
                    <td className="p-3">{formatScoreCompact(row.dyspnee_score)}</td>
                    <td className="p-3 text-slate-600">{row.temperature || "—"}</td>
                    <td className="p-3 text-slate-600">{row.spo2 || "—"}</td>
                    <td className="p-3">
                      <AlertBadge level={row.alert_level} triggered={row.alert_generated} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t bg-slate-50">
            <span className="text-sm text-slate-500">
              Page {data.page} sur {data.total_pages} ({data.total} résultats)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
